/**
 * @fileoverview Escalation Version History Modal
 * @module components/modals/EscalationVersionHistoryModal
 * @description Lists an escalation's current record plus every prior
 * version, newest updatedAt first (oldest last) regardless of the order
 * `versions` arrives in. Each row shows only who last updated that version
 * and when, plus an edit (current record, non-terminal status) or view
 * (everything else) icon button that opens it in CreateEscalationModal -
 * editable only when it's the current record AND its status hasn't reached a
 * terminal state (Resolved/Closed/Canceled), since those lock the whole
 * record.
 */

import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Card,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import formatDateTimeUtil from "../../utils/formatDateTime";
import { truncateText } from "../../utils/formatUtils";
import { placeholderText } from "../../constants/colors";
import { TERMINAL_ESCALATION_STATUSES } from "../../constants/escalation";

const EscalationVersionHistoryModal = ({
  open,
  onClose,
  versions = [],
  onSelectVersion,
}) => {
  if (!open) return null;

  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Sorted defensively here rather than trusting the order `versions`
  // arrives in - newest updatedAt first, oldest last. Sorting must use
  // updatedAt, not createdAt - history entries can share the same createdAt
  // as the original record (see fetchEscalationVersions), so createdAt can't
  // reliably order them even though it's fine to display.
  const sortedVersions = [...versions].sort((a, b) => {
    const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });
  // Escalation Type/Reason are locked once created (see CreateEscalationModal
  // - both fields are disabled in edit mode), so every version shares the
  // same values - any one of them works as the source for the title.
  const { escalationType, escalationReason } = sortedVersions[0] || {};
  const titlePrefix = [escalationType, escalationReason]
    .filter(Boolean)
    .join(" - ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted={false}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            padding: { xs: "16px", md: "24px" },
            backgroundColor: "#F7F8FB",
            boxShadow: "none",
            border: "none",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
            wordBreak: "break-word",
          }}
        >
          {titlePrefix
            ? `${titlePrefix} - Escalation History`
            : "Escalation History"}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close escalation history dialog"
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon sx={{ color: "#7F7C9F" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            maxHeight: "60vh",
            overflowY: "auto",
            p: "4px",
            pr: "10px",
            scrollbarWidth: "thin",
            scrollbarColor: `${placeholderText} #f1f1f1`,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: `${placeholderText} !important`,
              borderRadius: "4px",
              backgroundClip: "padding-box",
              border: `2px solid ${placeholderText}`,
              opacity: "1 !important",
              visibility: "visible !important",
            },
          }}
        >
          {sortedVersions.map((version, index) => {
            // The earliest version (last in this descending-sorted list) is
            // the escalation's original creation, not an edit of anything -
            // show who created it and when, from createdBy/createdAt,
            // instead of the updatedBy/updatedAt every later version uses.
            const isEarliest = index === sortedVersions.length - 1;
            // Historical /history rows don't reliably carry their own
            // updatedAt - fall back to createdAt so the timestamp always has
            // something to show rather than displaying nothing.
            const displayDateTime = formatDateTimeUtil(
              isEarliest
                ? version.createdAt
                : version.updatedAt || version.createdAt,
              { timeZone: systemTimeZone },
            );
            const personName = isEarliest
              ? version.createdBy || "Unknown"
              : version.updatedBy || version.createdBy || "Unknown";
            const isEditable =
              version.isCurrent &&
              !TERMINAL_ESCALATION_STATUSES.includes(version.status);
            // Which reason applies depends on the version's status.
            let reasonLabel = "Escalation Reason";
            let reasonText = version.escalationReason;
            if (version.status === "Resolved") {
              reasonLabel = "Resolved Reason";
              reasonText = version.resolution;
            } else if (version.status === "Canceled") {
              reasonLabel = "Canceled Reason";
              reasonText = version.cancellationReason;
            }

            return (
              <Card
                key={version.id ?? version.createdAt ?? index}
                elevation={0}
                sx={{
                  width: "100%",
                  textAlign: "left",
                  border: "1px solid #E8E8E9",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  padding: "16px",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  "&:hover": {
                    boxShadow: "0 2px 10px rgba(64, 60, 111, 0.08)",
                    borderColor: "#D8D7E6",
                  },
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#403C6F",
                        fontWeight: 600,
                      }}
                    >
                      {isEarliest ? "Created By" : "Updated By"}: {personName}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "13px", color: "#7F7C9F", mt: 0.25 }}
                    >
                      {isEarliest ? "Created On" : "Updated On"}:{" "}
                      {displayDateTime}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#1B2F3F",
                        mt: 0.25,
                        wordBreak: "break-word",
                      }}
                    >
                      {reasonLabel}: {truncateText(reasonText, 120) || "N/A"}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => onSelectVersion(version)}
                    aria-label={
                      isEditable
                        ? "Edit this escalation version"
                        : "View this escalation version"
                    }
                    sx={{
                      color: isEditable ? "#403C6F" : "#7F7C9F",
                      backgroundColor: "#F7F8FB",
                      "&:hover": { backgroundColor: "#ECECF1" },
                    }}
                  >
                    {isEditable ? (
                      <EditIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Card>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

EscalationVersionHistoryModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      escalationType: PropTypes.string,
      escalationReason: PropTypes.string,
      description: PropTypes.string,
      followup: PropTypes.string,
      status: PropTypes.string,
      resolution: PropTypes.string,
      cancellationReason: PropTypes.string,
      ownerName: PropTypes.string,
      createdBy: PropTypes.string,
      createdAt: PropTypes.string,
      updatedBy: PropTypes.string,
      updatedAt: PropTypes.string,
      isCurrent: PropTypes.bool,
    }),
  ),
  onSelectVersion: PropTypes.func.isRequired,
};

export default EscalationVersionHistoryModal;
